import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";

import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import ScrollToTop from "@/components/common/ScrollToTop";
import { PageTracker } from "@/lib/tracker";

import Home from "@/pages/Home";
import AutodestruccionSA from "@/pages/AutodestruccionSA";
import Libro from "@/pages/Libro";
import Descargar from "@/pages/Descargar";
import Gracias from "@/pages/Gracias";
import SeoLanding from "@/pages/SeoLanding";
import {
    LIBROS_ADICCIONES_CONFIG,
    LIBRO_SOBRIEDAD_CONFIG,
    CUENTOS_ADICTOS_CONFIG,
} from "@/data/seoLandings";
import Productos from "@/pages/Productos";
import Planes from "@/pages/Planes";
import Equipo from "@/pages/Equipo";
import Glosario from "@/pages/Glosario";
import Antipoesia from "@/pages/Antipoesia";
import Curriculum from "@/pages/Curriculum";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Sobre from "@/pages/Sobre";
import Contacto from "@/pages/Contacto";
import Salida from "@/pages/Salida";
import Login from "@/pages/Login";
import Admin from "@/pages/Admin";
import AdminBlog from "@/pages/AdminBlog";
import AdminAntipoesia from "@/pages/AdminAntipoesia";
import AdminGlosario from "@/pages/AdminGlosario";
import AdminMapaInverso from "@/pages/AdminMapaInverso";
import AdminOrders from "@/pages/AdminOrders";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminTestimonios from "@/pages/AdminTestimonios";
import NoMasCorreos from "@/pages/NoMasCorreos";
import MapaInverso from "@/pages/MapaInverso";
import NotFound from "@/pages/NotFound";

function Protected({ children }) {
    const { user } = useAuth();
    if (user === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#111]">
                <p className="font-mono text-[var(--ink-muted)] uppercase tracking-[0.3em] text-xs">
                    Verificando expediente…
                </p>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

function Shell() {
    return (
        <>
            <ScrollToTop />
            <PageTracker />
            <Navbar />
            <main>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/libro" element={<Libro />} />
                    <Route path="/descargar" element={<Descargar />} />
                    <Route
                        path="/transparencia"
                        element={<Navigate to="/descargar" replace />}
                    />
                    <Route
                        path="/comprar"
                        element={<Navigate to="/descargar" replace />}
                    />
                    <Route
                        path="/libros-de-adicciones"
                        element={<SeoLanding config={LIBROS_ADICCIONES_CONFIG} />}
                    />
                    <Route
                        path="/libro-de-sobriedad"
                        element={<SeoLanding config={LIBRO_SOBRIEDAD_CONFIG} />}
                    />
                    <Route
                        path="/cuentos-para-adictos"
                        element={<SeoLanding config={CUENTOS_ADICTOS_CONFIG} />}
                    />
                    {/* Alias antiguos redirigen a la landing propia */}
                    <Route
                        path="/libros-adicciones"
                        element={<Navigate to="/libros-de-adicciones" replace />}
                    />
                    <Route path="/gracias" element={<Gracias />} />
                    <Route path="/autodestruccion-sa" element={<AutodestruccionSA />} />
                    <Route path="/productos" element={<Productos />} />
                    <Route path="/planes" element={<Planes />} />
                    <Route path="/equipo" element={<Equipo />} />
                    <Route path="/glosario" element={<Glosario />} />
                    <Route path="/antipoesia" element={<Antipoesia />} />
                    <Route path="/curriculum" element={<Curriculum />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route
                        path="/nelson-gonzalez-rodriguez"
                        element={<Sobre />}
                    />
                    {/* Redirects 301 desde URLs antiguas para preservar SEO */}
                    <Route
                        path="/sobre"
                        element={<Navigate to="/nelson-gonzalez-rodriguez" replace />}
                    />
                    <Route
                        path="/nelson-gonzalez-quien-soy"
                        element={<Navigate to="/nelson-gonzalez-rodriguez" replace />}
                    />
                    <Route
                        path="/quien-soy"
                        element={<Navigate to="/nelson-gonzalez-rodriguez" replace />}
                    />
                    <Route path="/contacto" element={<Contacto />} />
                    <Route path="/salida" element={<Salida />} />
                    <Route path="/mapa-inverso" element={<MapaInverso />} />
                    <Route path="/login" element={<Login />} />
                    <Route
                        path="/admin"
                        element={
                            <Protected>
                                <Admin />
                            </Protected>
                        }
                    />
                    <Route
                        path="/admin/blog"
                        element={
                            <Protected>
                                <AdminBlog />
                            </Protected>
                        }
                    />
                    <Route
                        path="/admin/antipoesia"
                        element={
                            <Protected>
                                <AdminAntipoesia />
                            </Protected>
                        }
                    />
                    <Route
                        path="/admin/glosario"
                        element={
                            <Protected>
                                <AdminGlosario />
                            </Protected>
                        }
                    />
                    <Route
                        path="/admin/mapa-inverso"
                        element={
                            <Protected>
                                <AdminMapaInverso />
                            </Protected>
                        }
                    />
                    <Route
                        path="/admin/pedidos"
                        element={
                            <Protected>
                                <AdminOrders />
                            </Protected>
                        }
                    />
                    <Route
                        path="/admin/analytics"
                        element={
                            <Protected>
                                <AdminAnalytics />
                            </Protected>
                        }
                    />
                    <Route
                        path="/admin/testimonios"
                        element={
                            <Protected>
                                <AdminTestimonios />
                            </Protected>
                        }
                    />
                    <Route path="/no-mas-correos" element={<NoMasCorreos />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </main>
            <Footer />
        </>
    );
}

export default function App() {
    return (
        <HelmetProvider>
            <AuthProvider>
                <BrowserRouter>
                    <div className="App">
                        <Shell />
                        <Toaster
                            position="top-center"
                            theme="dark"
                            toastOptions={{
                                style: {
                                    background: "#1a1a1a",
                                    color: "#E8E6E1",
                                    border: "1px solid #2a2a2a",
                                    borderRadius: 0,
                                    fontFamily: "IBM Plex Mono, monospace",
                                    fontSize: "13px",
                                },
                            }}
                        />
                    </div>
                </BrowserRouter>
            </AuthProvider>
        </HelmetProvider>
    );
}
